import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import ReactSelect from 'react-select';
import { useUser } from '@clerk/clerk-react';
import { v4 as uuid } from 'uuid';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import {
  Upload,
  Trash2,
  X,
  Image as ImageIcon,
  Search,
  Grid3X3,
  Building2,
  Smartphone,
  ImagePlus,
  FolderOpen,
  CheckCircle2,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import {
  selectedClientSelector,
  setSelectedClient
} from '../../store/slices/selectedClient';
import { showLoading, hideLoading } from '../../store/slices/loadingState';
import { clientsSelector } from '../../store/slices/clients';

import {
  API_KEY,
  API_CLIENTS_NAME,
  UPDATE_STOCK_CLIENT_UPLOAD_IMAGES,
  DELETE_CL_IMAGE,
  DELETE_IMAGES,
  LIST_CL_IMAGES,
  LIST_CL_IMAGES_GOOGLE,
  LIST_CL_IMAGES_GOOGLE_BUSINESS,
  LIST_CL_IMAGES_YELP,
  BUCKET_CS_IMAGES_UPLOAD,
  S3_GET_PRESIGNED_URLS,
  APP_S3,
  MANIPULATE_DESCRIPTION,
  API_GET_CS_IMAGES,
  LOAD_IMAGES_API
} from '../../utils/constants';
import { authSelector } from '../../store/slices/auth';
import LoadingSpinner from '../../pages/Calendar/GallerySection/StockImagesComponents/Partials/LoadingSpinner';
import Swal from 'sweetalert2';

const Images = () => {
  const dispatch = useDispatch();
  const { user } = useUser();

  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);
  const { userInfo } = useSelector(authSelector);

  // Determine if user is superAdmin based on domain
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  // Use the selected client's ID
  const clientId = selectedClient?.id || null;

  const [selectedImages, setSelectedImages] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Combined loading state - true when either initial load or filtering
  const isLoadingData = loading || initialLoading;

  const [imageSource, setImageSource] = useState('all');
  const [imageStats, setImageStats] = useState({
    client: 0,
    gbp: 0,
    app: 0,
    stock: 0
  });

  const [files, setFiles] = useState({ length: 0 });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [deleteErrorModalOpen, setDeleteErrorModalOpen] = useState(false);
  const [deleteMultipleConfirmModalOpen, setDeleteMultipleConfirmModalOpen] =
    useState(false);
  const [deleteMultipleErrorModalOpen, setDeleteMultipleErrorModalOpen] =
    useState(false);
  const [uploadErrorModalOpen, setUploadErrorModalOpen] = useState(false);
  const [resyncLoading, setResyncLoading] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    () => window.innerWidth < 640
  );
  const [clientNavigationMode, setClientNavigationMode] = useState('all');

  const hasNoAIFlag = (customer) => {
    const flags = customer?.clientFlags;
    if (Array.isArray(flags)) {
      return flags.includes('NO_AI');
    }
    if (typeof flags === 'string') {
      return flags
        .split(',')
        .map((flag) => flag.trim())
        .includes('NO_AI');
    }
    return false;
  };

  const noAIClientList = useMemo(
    () => customerList.filter((customer) => hasNoAIFlag(customer)),
    [customerList]
  );

  const navigableCustomerList =
    clientNavigationMode === 'no_ai' ? noAIClientList : customerList;

  const searchableCustomerList = useMemo(
    () =>
      navigableCustomerList.map((customer) => ({
        ...customer,
        label: customer.clientName,
        value: customer.clientName
      })),
    [navigableCustomerList]
  );
  const sourceLabels = {
    all: {
      desktop: 'Client Images',
      mobile: 'Client',
      title: 'All Client Images',
      mobileTitle: 'Client Images'
    },
    gbp: {
      desktop: 'Google Business Images',
      mobile: 'Google',
      title: 'Google Business Images',
      mobileTitle: 'Google Images'
    },
    app: {
      desktop: 'Customer App Uploads',
      mobile: 'App',
      title: 'Customer App Uploads',
      mobileTitle: 'App Uploads'
    },
    stock: {
      desktop: 'Stock Images',
      mobile: 'Stock',
      title: 'Stock Images',
      mobileTitle: 'Stock Images'
    }
  };
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 640);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (clientNavigationMode !== 'no_ai') return;
    if (!selectedClient?.id) return;
    if (hasNoAIFlag(selectedClient)) return;
    if (!noAIClientList.length) return;

    handleChangeClient(noAIClientList[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientNavigationMode, selectedClient?.id, noAIClientList]);

  // Handle change in client search select
  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;

    try {
      // Fetch complete client data from API to ensure all attributes are present
      const encodedClientName = encodeURIComponent(selectedOption.clientName);
      const response = await axios.get(
        `${API_CLIENTS_NAME}${encodedClientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );

      if (response?.data) {
        // Update selected client in Redux with complete data including socialnetworks
        dispatch(setSelectedClient(response.data));
      } else {
        // Fallback to using the client from customerList if API call returns no data
        dispatch(setSelectedClient(selectedOption));
      }
    } catch (error) {
      console.error('Error fetching complete client data:', error);
      // Fallback to using the client from customerList if API call fails
      dispatch(setSelectedClient(selectedOption));
    }
  };

  const fetchImages = async (source) => {
    dispatch(showLoading());
    setLoading(true);
    setSelectedImages([]);

    if (!clientId) {
      dispatch(hideLoading());
      setLoading(false);
      setInitialLoading(false);
      return;
    }

    try {
      let fetchedImages = [];

      // Handle App Images separately using APP_S3 endpoint
      if (source === 'app') {
        const response = await axios.get(`${APP_S3}?userId=${clientId}`);

        if (response.data.body && response.data.body.length) {
          // Get descriptions
          const descConfig = {
            method: 'post',
            url: MANIPULATE_DESCRIPTION,
            data: {
              userid: clientId,
              get: true,
              to_suite: true
            }
          };

          const descResponse = await axios(descConfig);
          const allDescriptions = descResponse.data?.images || null;

          // Format images to match the expected structure
          fetchedImages = response.data.body.map((img) => {
            const url = img.url.replace('/thumbnails', '');
            return {
              SK: url,
              sourceUrl: url,
              source: 'app',
              LastModified: img.LastModified,
              description:
                allDescriptions && allDescriptions[img.url]
                  ? typeof allDescriptions[img.url] === 'object'
                    ? allDescriptions[img.url].description
                    : allDescriptions[img.url]
                  : null,
              isUsed:
                allDescriptions && allDescriptions[img.url]
                  ? allDescriptions[img.url].isUsed
                  : false
            };
          });
        }
      } else if (source === 'stock') {
        // Handle Stock Images from Content Strategy
        const contentStrategyId = selectedClient?.contentStrategy;

        if (!contentStrategyId) {
          console.warn('No content strategy ID found for client');
          fetchedImages = [];
        } else {
          const response = await axios.get(
            `${API_GET_CS_IMAGES}/${contentStrategyId}`
          );

          if (response.data && response.data.length) {
            // Format stock images to match expected structure
            fetchedImages = response.data.map((img) => ({
              SK: img.url || img.SK,
              sourceUrl: img.url || img.SK,
              source: 'stock',
              fileName: img.fileName,
              fileSize: img.fileSize,
              createdAt: img.createdAt
            }));
          }
        }
      } else {
        // Handle other sources with existing logic
        let url;
        switch (source) {
          case 'google':
            url = `${LIST_CL_IMAGES_GOOGLE}/${clientId}`;
            break;
          case 'gbp':
            url = `${LIST_CL_IMAGES_GOOGLE_BUSINESS}/${clientId}`;
            break;
          case 'yelp':
            url = `${LIST_CL_IMAGES_YELP}/${clientId}`;
            break;
          case 'all':
          default:
            url = `${LIST_CL_IMAGES}/${clientId}`;
            break;
        }

        const response = await axios.get(url);
        fetchedImages = response.data || [];
      }

      // Sort images by date (most recent first)
      const sortedImages = fetchedImages.sort((a, b) => {
        // Para Client Images: lastUse (segundos) -> convertir a milisegundos
        // Para App Images: LastModified (ISO string)
        // Para Stock Images: createdAt
        const dateA = a.lastUse
          ? a.lastUse * 1000
          : a.LastModified || a.createdAt || 0;
        const dateB = b.lastUse
          ? b.lastUse * 1000
          : b.LastModified || b.createdAt || 0;

        // Convert to timestamp if they're strings
        const timeA =
          typeof dateA === 'string' ? new Date(dateA).getTime() : dateA || 0;
        const timeB =
          typeof dateB === 'string' ? new Date(dateB).getTime() : dateB || 0;

        // Sort descending (newest first)
        return timeB - timeA;
      });

      setImages(sortedImages);

      // Fetch stats for Library Overview - Always calculate stats
      try {
        // Fetch all stats in parallel
        const [clientImagesRes, gbpRes, appRes, stockRes] = await Promise.all([
          axios.get(`${LIST_CL_IMAGES}/${clientId}`),
          axios.get(`${LIST_CL_IMAGES_GOOGLE_BUSINESS}/${clientId}`),
          axios.get(`${APP_S3}?userId=${clientId}`),
          selectedClient?.contentStrategy
            ? axios.get(
                `${API_GET_CS_IMAGES}/${selectedClient.contentStrategy}`
              )
            : Promise.resolve({ data: [] })
        ]);

        const clientCount = clientImagesRes.data?.length || 0;
        const gbpCount = gbpRes.data?.length || 0;
        const appCount = appRes.data?.body?.length || 0;
        const stockCount = stockRes.data?.length || 0;

        setImageStats({
          client: clientCount,
          gbp: gbpCount,
          app: appCount,
          stock: stockCount
        });
      } catch (statsError) {
        console.error('Error fetching image stats:', statsError);
        setImageStats({
          client: 0,
          gbp: 0,
          app: 0,
          stock: 0
        });
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      setImages([]);
      setImageStats({
        client: 0,
        gbp: 0,
        app: 0,
        stock: 0
      });
    } finally {
      dispatch(hideLoading());
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleImageSourceChange = (source) => {
    setImageSource(source);
    fetchImages(source);
  };

  useEffect(() => {
    fetchImages('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleDeleteImage = (image) => {
    setImageToDelete(image);
    setDeleteConfirmModalOpen(true);
  };

  const confirmDeleteImage = async () => {
    if (!imageToDelete) return;

    try {
      // Use different delete endpoint for app images
      if (imageToDelete.source === 'app') {
        await handleDeleteAppImage(imageToDelete);
      } else {
        await axios({
          method: 'post',
          url: DELETE_CL_IMAGE,
          headers: { 'Content-Type': 'application/json' },
          data: JSON.stringify({
            clientId: imageToDelete.PK || clientId,
            sourceUrl: imageToDelete.sourceUrl || imageToDelete.SK
          })
        });

        const imageUrl = imageToDelete.sourceUrl || imageToDelete.SK;

        setImages((prev) =>
          prev.filter((img) => {
            const currentUrl = img.sourceUrl || img.SK;
            return currentUrl !== imageUrl;
          })
        );

        setSelectedImages((prevSelected) => {
          const imgIndex = images.findIndex((img) => {
            const currentUrl = img.sourceUrl || img.SK;
            return currentUrl === imageUrl;
          });
          return prevSelected.filter((idx) => idx !== imgIndex);
        });
      }

      setDeleteConfirmModalOpen(false);
      setImageToDelete(null);
    } catch (error) {
      console.error('Error deleting image:', error);
      setDeleteConfirmModalOpen(false);
      setImageToDelete(null);
      setDeleteErrorModalOpen(true);
    }
  };

  const handleDeleteSelected = () => {
    setDeleteMultipleConfirmModalOpen(true);
  };

  const confirmDeleteMultiple = async () => {
    try {
      const deletePromises = selectedImages.map((idx) => {
        const imageToDelete = images[idx];

        // Use different delete logic for app images
        if (imageToDelete.source === 'app') {
          return handleDeleteAppImage(imageToDelete);
        } else {
          return axios({
            method: 'post',
            url: DELETE_CL_IMAGE,
            headers: { 'Content-Type': 'application/json' },
            data: JSON.stringify({
              clientId: imageToDelete.PK || clientId,
              sourceUrl: imageToDelete.sourceUrl || imageToDelete.SK
            })
          });
        }
      });

      await Promise.all(deletePromises);

      setSelectedImages([]);
      setDeleteMultipleConfirmModalOpen(false);
    } catch (error) {
      console.error('Error deleting images:', error);
      setDeleteMultipleConfirmModalOpen(false);
      setDeleteMultipleErrorModalOpen(true);
    }
  };

  const handleUploadFiles = async () => {
    setUploadingImages(true);
    setProgress(0);
    const uploadedImages = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileType = file.type;

        const sanitizedFileName = file.name
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9.-]/g, '_')
          .replace(/\s+/g, '_');

        const fileName = `${uuid()}-${sanitizedFileName}`;

        const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bucketName: BUCKET_CS_IMAGES_UPLOAD,
            files: [{ fileName, fileType }]
          })
        });

        if (!presignedResponse.ok)
          throw new Error('Failed to get presigned URL');

        const { urls } = await presignedResponse.json();
        const presignedUrl = urls?.[0]?.url;

        if (!presignedUrl) throw new Error('No presigned URL returned');

        const uploadResult = await fetch(presignedUrl, {
          method: 'PUT',
          headers: { 'Content-Type': fileType },
          body: file
        });

        if (!uploadResult.ok) throw new Error('File upload to S3 failed');

        const uploadedFileUrl = `https://${BUCKET_CS_IMAGES_UPLOAD}.s3.amazonaws.com/${fileName}`;

        // Get current timestamp in seconds (para que coincida con el formato del backend)
        const currentTimestampSeconds = Math.floor(Date.now() / 1000);

        await axios.post(
          UPDATE_STOCK_CLIENT_UPLOAD_IMAGES,
          {
            clientId,
            sourceUrl: uploadedFileUrl,
            source: 'manual_upload',
            keywords: '',
            lastUse: currentTimestampSeconds
          },
          {
            headers: {
              'X-API-KEY': API_KEY,
              'Content-Type': 'application/json'
            }
          }
        );

        // Add the uploaded image to the array with current timestamp
        uploadedImages.push({
          SK: uploadedFileUrl,
          sourceUrl: uploadedFileUrl,
          source: 'manual_upload',
          lastUse: currentTimestampSeconds
        });

        setProgress(Math.round(((i + 1) / files.length) * 100));
      }

      // Immediately refresh from server to get the updated images with correct lastUse
      setUploadingImages(false);
      setShowUploadModal(false);
      setFiles({ length: 0 });
      setProgress(0);

      // Refresh images from server
      await fetchImages(imageSource);
    } catch (error) {
      console.error('Error during upload:', error);
      setUploadErrorModalOpen(true);
      setUploadingImages(false);
    }
  };

  const getSourceLabel = () => {
    switch (imageSource) {
      case 'gbp':
        return 'Google Business Photos';
      case 'yelp':
        return 'Yelp Images';
      case 'all':
      default:
        return 'All Client Images';
    }
  };

  const renderUploadPreview = () => {
    const itemsList = [];
    for (let i = 0; i < files.length; i++) itemsList.push(files[i]);

    return itemsList.map((item, index) => {
      const fileSize =
        item.size < 1024
          ? `${item.size} bytes`
          : item.size < 1048576
          ? `${(item.size / 1024).toFixed(1)} KB`
          : `${(item.size / 1048576).toFixed(1)} MB`;

      const fileName =
        item.name.length > 35 ? `${item.name.substring(0, 35)}...` : item.name;

      return (
        <div
          key={index}
          className="flex items-center justify-between p-4 mb-2"
          style={{
            backgroundColor: '#f9fafb',
            borderRadius: '0.75rem',
            border: '1px solid #e5e7eb'
          }}>
          <div className="flex-1 min-w-0 mr-4">
            <p
              className="text-sm font-medium truncate"
              style={{ color: '#1f2937' }}>
              {fileName}
            </p>
            <p
              className="text-xs"
              style={{ color: '#6b7280', marginTop: '0.25rem' }}>
              {fileSize}
            </p>
          </div>
          <img
            src={URL.createObjectURL(item)}
            alt={`Preview ${index + 1}`}
            className="w-24 h-24 object-cover"
            style={{
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb'
            }}
          />
        </div>
      );
    });
  };

  const toggleImageSelection = (index) => {
    // No permitir selección en Stock Images
    if (imageSource === 'stock') return;

    setSelectedImages((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleDeleteAppImage = async (image) => {
    try {
      // Parse the URL to extract the required parts
      // URL format: https://bucket.s3.amazonaws.com/userid/path/to/image.jpg
      const divideUrl = image.sourceUrl.split('/');
      const user_id = divideUrl[4] + '/';
      const path_thumbnail = divideUrl[5] + '/' + divideUrl[6];
      const path_image = divideUrl[6];

      await axios.delete(DELETE_IMAGES, {
        data: {
          user_id: user_id,
          path_thumbnail: path_thumbnail,
          path_image: path_image
        }
      });

      // Remove the deleted image from the list
      const imageUrl = image.sourceUrl || image.SK;
      setImages((prev) =>
        prev.filter((img) => {
          const currentUrl = img.sourceUrl || img.SK;
          return currentUrl !== imageUrl;
        })
      );

      setSelectedImages((prevSelected) => {
        const imgIndex = images.findIndex((img) => {
          const currentUrl = img.sourceUrl || img.SK;
          return currentUrl === imageUrl;
        });
        return prevSelected.filter((idx) => idx !== imgIndex);
      });
    } catch (error) {
      console.error('Error deleting app image:', error);
      throw error;
    }
  };

  return (
    <div style={{ backgroundColor: '#f8f9fb', minHeight: '100vh' }}>
      {/* Upload Modal */}
      {files?.length > 0 && showUploadModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          style={{ backdropFilter: 'blur(4px)' }}>
          <div
            className="bg-card w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col"
            style={{
              borderRadius: '1rem',
              boxShadow:
                '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
              backgroundColor: 'white'
            }}>
            <div
              className="flex items-center justify-between p-6 border-b border-border"
              style={{ borderBottom: '1px solid #e5e7eb' }}>
              <h2
                className="text-xl font-semibold"
                style={{ color: '#1f2937' }}>
                Upload Images ({files.length})
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setFiles({ length: 0 });
                }}
                disabled={uploadingImages}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: 'transparent',
                  cursor: uploadingImages ? 'not-allowed' : 'pointer',
                  opacity: uploadingImages ? '0.5' : '1'
                }}
                onMouseEnter={(e) =>
                  !uploadingImages &&
                  (e.currentTarget.style.backgroundColor = '#f3f4f6')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = 'transparent')
                }>
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3">{renderUploadPreview()}</div>
            </div>

            <div
              className="flex items-center justify-end gap-3 p-6 border-t"
              style={{ borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setFiles({ length: 0 });
                }}
                disabled={uploadingImages}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: isMobileView ? '12px' : '14px',
                  fontWeight: '500',
                  cursor: uploadingImages ? 'not-allowed' : 'pointer',
                  opacity: uploadingImages ? '0.5' : '1'
                }}>
                Cancel
              </button>
              <button
                onClick={handleUploadFiles}
                disabled={uploadingImages || files.length === 0}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  backgroundColor:
                    uploadingImages || files.length === 0
                      ? '#9ca3af'
                      : '#3766a8',
                  color: 'white',
                  fontSize: isMobileView ? '12px' : '14px',
                  fontWeight: '500',
                  cursor:
                    uploadingImages || files.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                }}>
                {uploadingImages ? `Uploading... ${progress}%` : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main
        className="overflow-x-hidden"
        style={{
          padding: isMobileView ? '0.75rem' : '2.5rem',
          paddingBottom: isMobileView ? '1rem' : '1.5rem'
        }}>
        {/* Page Title */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 mb-2">
          <div className="flex items-center gap-4 min-w-0">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground break-words">
                Image Library, {selectedClient ? selectedClient.clientName : ''}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                Manage and organize all your media files in one place
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
            {isSuperAdmin && (
              <div className="hidden lg:block relative w-72">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                <ReactSelect
                  placeholder="Search"
                  onChange={(client) => handleChangeClient(client)}
                  options={searchableCustomerList}
                  hideSelectedOptions={true}
                  controlShouldRenderValue={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null
                  }}
                  openMenuOnClick={false}
                  openMenuOnFocus={false}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderRadius: '0.75rem',
                      borderWidth: '1px',
                      borderColor: state.isFocused
                        ? 'hsl(var(--primary))'
                        : 'hsl(var(--border))',
                      boxShadow: state.isFocused
                        ? '0 0 0 1px hsl(var(--primary))'
                        : 'none',
                      backgroundColor: '#ffffff',
                      paddingTop: '10px',
                      paddingBottom: '9px',
                      paddingLeft: '2.5rem',
                      fontSize: '1rem',
                      '&:hover': {
                        borderColor: 'hsl(var(--primary))'
                      }
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: 'hsl(var(--muted-foreground))',
                      fontSize: '1rem'
                    }),
                    input: (provided) => ({
                      ...provided,
                      fontSize: '1rem',
                      color: 'hsl(var(--foreground))'
                    }),
                    menu: (provided) => ({
                      ...provided,
                      borderRadius: '0.75rem',
                      overflow: 'hidden',
                      zIndex: 9999,
                      boxShadow:
                        '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                    }),
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 9999
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
                      color: state.isFocused ? '#1e293b' : '#64748b',
                      cursor: 'pointer',
                      padding: '12px 16px',
                      fontSize: '1rem'
                    })
                  }}
                />
              </div>
            )}
            {imageSource === 'all' && (
              <button
                onClick={() =>
                  document.getElementById('image_uploads')?.click()
                }
                style={{
                  backgroundColor: '#3766a8',
                  color: 'white',
                  padding: isMobileView ? '0 1.25rem' : '0 2rem',
                  borderRadius: '0.75rem',
                  boxShadow:
                    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                  border: 'none',
                  cursor: 'pointer',
                  height: isMobileView ? '48px' : '56px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  justifyContent: 'center',
                  fontSize: isMobileView ? '15px' : '18px',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  width: isMobileView ? '100%' : 'auto',
                  minWidth: isMobileView ? '0' : 'auto'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2d5389';
                  e.currentTarget.style.boxShadow =
                    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3766a8';
                  e.currentTarget.style.boxShadow =
                    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)';
                }}>
                <Upload className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>{isMobileView ? 'Upload' : 'Upload Images'}</span>
              </button>
            )}
          </div>
        </div>

        {isSuperAdmin &&
          (customerList.length === 0 ? (
            <LoadingSpinner />
          ) : (
            <div className="flex w-full items-center justify-end">
              <div className="inline-flex items-center rounded-xl border bg-muted/50 p-1 shadow-sm">
                <div className="inline-flex items-center rounded-lg bg-background/80 p-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={
                      `h-8 px-3 text-xs sm:text-sm rounded-md transition-all ` +
                      (clientNavigationMode === 'all'
                        ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                        : 'text-muted-foreground hover:text-foreground')
                    }
                    onClick={() => setClientNavigationMode('all')}>
                    All Clients
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={
                      `h-8 px-3 text-xs sm:text-sm rounded-md transition-all ` +
                      (clientNavigationMode === 'no_ai'
                        ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                        : 'text-muted-foreground hover:text-foreground')
                    }
                    onClick={() => setClientNavigationMode('no_ai')}
                    disabled={noAIClientList.length === 0}>
                    No AI Clients ({noAIClientList.length})
                  </Button>
                </div>

                {/* <div className="mx-2 h-6 w-px bg-border" /> */}

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    disabled={
                      navigableCustomerList.findIndex(
                        (customer) => customer.id === selectedClient.id
                      ) === 0
                    }
                    onClick={() => {
                      let x = navigableCustomerList.findIndex(
                        (customer) => customer.id === selectedClient.id
                      );
                      if (x > 0) {
                        handleChangeClient(navigableCustomerList[x - 1]);
                      }
                    }}>
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <span className="text-base font-medium text-foreground min-w-[60px] text-center">
                    {navigableCustomerList.length > 0
                      ? `${
                          navigableCustomerList.findIndex(
                            (customer) => customer.id === selectedClient.id
                          ) + 1
                        }/${navigableCustomerList.length}`
                      : '0/0'}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-10 w-10"
                    disabled={
                      navigableCustomerList.findIndex(
                        (customer) => customer.id === selectedClient.id
                      ) >=
                      navigableCustomerList.length - 1
                    }
                    onClick={() => {
                      let x = navigableCustomerList.findIndex(
                        (customer) => customer.id === selectedClient.id
                      );
                      if (x < navigableCustomerList.length - 1) {
                        handleChangeClient(navigableCustomerList[x + 1]);
                      }
                    }}>
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
      </main>

      {/* Navigation Menu - Sticky */}
      <div
        className="sticky top-0 z-10 overflow-x-hidden"
        style={{
          backgroundColor: '#f8f9fb',
          backdropFilter: 'blur(8px)',
          padding: isMobileView ? '0.5rem 0.75rem' : '0.75rem 2.5rem',
          marginBottom: isMobileView ? '1rem' : '2rem'
        }}>
        <div
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            border: '1px solid #e5e7eb',
            padding: '0.375rem',
            borderRadius: '0.75rem',
            width: isMobileView ? '100%' : 'fit-content',
            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
          }}>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <button
              onClick={() => handleImageSourceChange('all')}
              style={{
                padding: isMobileView ? '0.5rem 0.625rem' : '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: isMobileView ? '12px' : '14px',
                fontWeight: '500',
                backgroundColor:
                  imageSource === 'all' ? '#3766a8' : 'transparent',
                color: imageSource === 'all' ? 'white' : '#1f2937',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: isMobileView ? '0.375rem' : '0.5rem',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (imageSource !== 'all')
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                if (imageSource !== 'all')
                  e.currentTarget.style.backgroundColor = 'transparent';
              }}>
              <Grid3X3 className="h-4 w-4 shrink-0" />
              {isMobileView
                ? sourceLabels.all.mobile
                : sourceLabels.all.desktop}
            </button>

            <button
              onClick={() => handleImageSourceChange('gbp')}
              style={{
                padding: isMobileView ? '0.5rem 0.625rem' : '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: isMobileView ? '12px' : '14px',
                fontWeight: '500',
                backgroundColor:
                  imageSource === 'gbp' ? '#3766a8' : 'transparent',
                color: imageSource === 'gbp' ? 'white' : '#1f2937',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: isMobileView ? '0.375rem' : '0.5rem',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (imageSource !== 'gbp')
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                if (imageSource !== 'gbp')
                  e.currentTarget.style.backgroundColor = 'transparent';
              }}>
              <Building2 className="h-4 w-4 shrink-0" />
              {isMobileView
                ? sourceLabels.gbp.mobile
                : sourceLabels.gbp.desktop}
            </button>

            <button
              onClick={() => handleImageSourceChange('app')}
              style={{
                padding: isMobileView ? '0.5rem 0.625rem' : '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: isMobileView ? '12px' : '14px',
                fontWeight: '500',
                backgroundColor:
                  imageSource === 'app' ? '#3766a8' : 'transparent',
                color: imageSource === 'app' ? 'white' : '#1f2937',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: isMobileView ? '0.375rem' : '0.5rem',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (imageSource !== 'app')
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                if (imageSource !== 'app')
                  e.currentTarget.style.backgroundColor = 'transparent';
              }}>
              <Smartphone className="h-4 w-4 shrink-0" />
              {isMobileView
                ? sourceLabels.app.mobile
                : sourceLabels.app.desktop}
            </button>

            <button
              onClick={() => handleImageSourceChange('stock')}
              style={{
                padding: isMobileView ? '0.5rem 0.625rem' : '0.5rem 0.75rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: 'pointer',
                fontSize: isMobileView ? '12px' : '14px',
                fontWeight: '500',
                backgroundColor:
                  imageSource === 'stock' ? '#3766a8' : 'transparent',
                color: imageSource === 'stock' ? 'white' : '#1f2937',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: isMobileView ? '0.375rem' : '0.5rem',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (imageSource !== 'stock')
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
              }}
              onMouseLeave={(e) => {
                if (imageSource !== 'stock')
                  e.currentTarget.style.backgroundColor = 'transparent';
              }}>
              <ImagePlus className="h-4 w-4 shrink-0" />
              {isMobileView
                ? sourceLabels.stock.mobile
                : sourceLabels.stock.desktop}
            </button>
          </div>
        </div>
      </div>

      <input
        style={{ visibility: 'hidden', position: 'absolute' }}
        id="image_uploads"
        onChange={(e) => {
          setFiles(e.target.files);
          if (e.target.files?.length > 0) setShowUploadModal(true);
        }}
        type="file"
        multiple
        accept="image/png, image/jpeg"
      />

      {/* Content Area with proper spacing */}
      <div
        style={{
          padding: isMobileView
            ? '0 0.75rem 5.5rem 0.75rem'
            : '0 2.5rem 2.5rem 2.5rem'
        }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobileView ? '1rem' : '2rem'
          }}>
          {/* Library Overview */}
          <Card
            style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              border: 'none',
              boxShadow:
                '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
            }}>
            <div
              style={{
                padding: isMobileView
                  ? '1rem 1rem 0.75rem 1rem'
                  : '1.5rem 2rem 1rem 2rem'
              }}>
              <div className="flex items-center gap-3 sm:gap-4">
                <div
                  style={{
                    width: isMobileView ? '42px' : '48px',
                    height: isMobileView ? '42px' : '48px',
                    borderRadius: '0.75rem',
                    backgroundColor: '#eff6ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <FolderOpen
                    style={{
                      width: isMobileView ? '20px' : '24px',
                      height: isMobileView ? '20px' : '24px',
                      color: '#3766a8'
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3
                    style={{
                      fontSize: isMobileView ? '18px' : '20px',
                      fontWeight: '700',
                      color: '#1a1a1a',
                      margin: 0,
                      lineHeight: '1.2',
                      fontFamily: 'inherit'
                    }}>
                    Library Overview
                  </h3>
                  <p
                    style={{
                      fontSize: isMobileView ? '14px' : '16px',
                      color: '#6b7280',
                      marginTop: '0.25rem',
                      marginBottom: 0,
                      lineHeight: '1.5',
                      fontFamily: 'inherit'
                    }}>
                    Quick summary of your image collection
                  </p>
                </div>
              </div>
            </div>
            <div
              style={{
                padding: isMobileView ? '0 1rem 1rem 1rem' : '0 2rem 2rem 2rem'
              }}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
                <div
                  style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.75rem',
                    padding: isMobileView ? '0.75rem' : '1rem',
                    textAlign: 'center'
                  }}>
                  {isLoadingData ? (
                    <>
                      <div className="h-10 w-16 bg-gray-200 rounded animate-pulse mx-auto mb-2" />
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mx-auto" />
                    </>
                  ) : (
                    <>
                      <p
                        style={{
                          fontSize: isMobileView ? '24px' : '30px',
                          fontWeight: '700',
                          color: '#3766a8',
                          margin: 0
                        }}>
                        {imageStats.client}
                      </p>
                      <p
                        style={{
                          fontSize: isMobileView ? '12px' : '14px',
                          color: '#6b7280',
                          marginTop: '0.25rem',
                          margin: 0
                        }}>
                        Client Images
                      </p>
                    </>
                  )}
                </div>

                <div
                  style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.75rem',
                    padding: isMobileView ? '0.75rem' : '1rem',
                    textAlign: 'center'
                  }}>
                  {isLoadingData ? (
                    <>
                      <div className="h-10 w-16 bg-gray-200 rounded animate-pulse mx-auto mb-2" />
                      <div className="h-4 w-28 bg-gray-200 rounded animate-pulse mx-auto" />
                    </>
                  ) : (
                    <>
                      <p
                        style={{
                          fontSize: isMobileView ? '24px' : '30px',
                          fontWeight: '700',
                          color: '#3766a8',
                          margin: 0
                        }}>
                        {imageStats.gbp}
                      </p>
                      <p
                        style={{
                          fontSize: isMobileView ? '12px' : '14px',
                          color: '#6b7280',
                          marginTop: '0.25rem',
                          margin: 0
                        }}>
                        Google Business
                      </p>
                    </>
                  )}
                </div>

                <div
                  style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.75rem',
                    padding: isMobileView ? '0.75rem' : '1rem',
                    textAlign: 'center'
                  }}>
                  {isLoadingData ? (
                    <>
                      <div className="h-10 w-16 bg-gray-200 rounded animate-pulse mx-auto mb-2" />
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mx-auto" />
                    </>
                  ) : (
                    <>
                      <p
                        style={{
                          fontSize: isMobileView ? '24px' : '30px',
                          fontWeight: '700',
                          color: '#3766a8',
                          margin: 0
                        }}>
                        {imageStats.app}
                      </p>
                      <p
                        style={{
                          fontSize: isMobileView ? '12px' : '14px',
                          color: '#6b7280',
                          marginTop: '0.25rem',
                          margin: 0
                        }}>
                        App Uploads
                      </p>
                    </>
                  )}
                </div>

                <div
                  style={{
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.75rem',
                    padding: isMobileView ? '0.75rem' : '1rem',
                    textAlign: 'center'
                  }}>
                  {isLoadingData ? (
                    <>
                      <div className="h-10 w-16 bg-gray-200 rounded animate-pulse mx-auto mb-2" />
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mx-auto" />
                    </>
                  ) : (
                    <>
                      <p
                        style={{
                          fontSize: isMobileView ? '24px' : '30px',
                          fontWeight: '700',
                          color: '#3766a8',
                          margin: 0
                        }}>
                        {imageStats.stock}
                      </p>
                      <p
                        style={{
                          fontSize: isMobileView ? '12px' : '14px',
                          color: '#6b7280',
                          marginTop: '0.25rem',
                          margin: 0
                        }}>
                        Stock Images
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Image Gallery Card */}
          <Card
            style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              border: 'none',
              boxShadow:
                '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
            }}>
            <div
              style={{
                padding: isMobileView
                  ? '1rem 1rem 0.75rem 1rem'
                  : '1.5rem 2rem 1rem 2rem'
              }}>
              <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                <div
                  style={{
                    width: isMobileView ? '42px' : '48px',
                    height: isMobileView ? '42px' : '48px',
                    borderRadius: '0.75rem',
                    backgroundColor: '#eff6ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <ImageIcon
                    width="24"
                    height="24"
                    style={{ color: '#3766a8' }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3
                    style={{
                      fontSize: isMobileView ? '18px' : '20px',
                      fontWeight: '700',
                      color: '#1a1a1a',
                      margin: 0,
                      lineHeight: '1.2',
                      fontFamily: 'inherit'
                    }}>
                    {isMobileView
                      ? sourceLabels[imageSource]?.mobileTitle
                      : sourceLabels[imageSource]?.title}
                  </h3>
                  <p
                    style={{
                      fontSize: isMobileView ? '14px' : '16px',
                      color: '#6b7280',
                      marginTop: '0.25rem',
                      marginBottom: 0,
                      lineHeight: '1.5',
                      fontFamily: 'inherit'
                    }}>
                    {imageSource === 'all'
                      ? 'Browse and manage images uploaded for this client'
                      : imageSource === 'gbp'
                      ? 'Images synced from your Google Business Profile'
                      : imageSource === 'app'
                      ? 'Images uploaded directly to the application'
                      : 'Professional stock images for your marketing'}
                  </p>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                  {imageSource === 'gbp' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm font-medium gap-1.5"
                      disabled={resyncLoading}
                      onClick={async () => {
                        if (!clientId) return;
                        setResyncLoading(true);
                        try {
                          for (let i = 0; i < 2; i++) {
                            await axios.post(
                              LOAD_IMAGES_API,
                              { clientId },
                              {
                                headers: {
                                  'Content-Type': 'application/json'
                                }
                              }
                            );
                          }
                          Swal.fire({
                            icon: 'success',
                            title: 'Sync Complete',
                            text: 'Image synchronization has finished successfully.',
                            confirmButtonText: 'OK'
                          }).then(() => {
                            window.location.reload();
                          });
                        } catch (error) {
                          Swal.fire({
                            icon: 'error',
                            title: 'Sync Failed',
                            text: 'An error occurred while syncing images. Please try again.',
                            confirmButtonText: 'OK'
                          });
                        } finally {
                          setResyncLoading(false);
                        }
                      }}>
                      {resyncLoading ? (
                        <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      )}
                      {resyncLoading ? 'Syncing...' : 'Resync Images'}
                    </Button>
                  )}
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      {images.length}
                    </span>{' '}
                    images.
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                padding: isMobileView ? '0 1rem 1rem 1rem' : '0 2rem 2rem 2rem'
              }}>
              {isLoadingData && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
                  {Array.from({ length: 12 }).map((_, idx) => (
                    <div
                      key={idx}
                      className="h-32 sm:h-48 bg-gray-200 rounded-lg animate-pulse"
                      style={{ borderRadius: '0.75rem' }}
                    />
                  ))}
                </div>
              )}

              {!isLoadingData && images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4">
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className="relative group overflow-hidden border-2 transition-all cursor-pointer"
                      style={{
                        borderRadius: '0.75rem',
                        borderColor: selectedImages.includes(index)
                          ? '#3766a8'
                          : 'transparent',
                        boxShadow: selectedImages.includes(index)
                          ? '0 0 0 2px rgba(55, 102, 168, 0.2)'
                          : 'none',
                        cursor:
                          imageSource === 'stock' || imageSource === 'app'
                            ? 'default'
                            : 'pointer'
                      }}
                      onClick={() => toggleImageSelection(index)}
                      onMouseEnter={(e) => {
                        const deleteBtn = e.currentTarget.querySelector(
                          'button[data-delete-btn]'
                        );
                        if (deleteBtn) deleteBtn.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        const deleteBtn = e.currentTarget.querySelector(
                          'button[data-delete-btn]'
                        );
                        if (deleteBtn) deleteBtn.style.opacity = '0';
                      }}>
                      <img
                        src={image.sourceUrl || image.SK}
                        alt={getSourceLabel()}
                        className={`w-full h-28 sm:h-48 object-cover transition-transform ${
                          imageSource !== 'stock' ? 'group-hover:scale-105' : ''
                        }`}
                        onError={(e) => {
                          e.target.src =
                            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2U1ZTdlYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
                        }}
                      />
                      {imageSource !== 'stock' && (
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      )}

                      {imageSource !== 'stock' && (
                        <button
                          data-delete-btn="true"
                          className="absolute top-2 right-2 h-8 w-8 rounded-full flex items-center justify-center transition-opacity"
                          style={{
                            backgroundColor: '#ef4444',
                            color: 'white',
                            opacity: isMobileView ? '1' : '0',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc2626';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ef4444';
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(image);
                          }}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}

                      {selectedImages.includes(index) && (
                        <div
                          className="absolute top-3 left-3 h-8 w-8 flex items-center justify-center"
                          style={{
                            backgroundColor: '#3766a8',
                            color: 'white',
                            borderRadius: '0.5rem'
                          }}>
                          <CheckCircle2 className="h-5 w-5" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {!isLoadingData && images.length === 0 && (
                <div className="text-center py-12">
                  <ImageIcon
                    className="h-16 w-16 mx-auto mb-4"
                    style={{ color: '#9ca3af' }}
                  />
                  <h3
                    className="text-lg font-semibold mb-2"
                    style={{ color: '#1f2937' }}>
                    No images found
                  </h3>
                  <p
                    className="mb-6"
                    style={{ color: '#6b7280', fontSize: '14px' }}>
                    Upload some images to get started
                  </p>
                  <button
                    onClick={() =>
                      document.getElementById('image_uploads')?.click()
                    }
                    style={{
                      backgroundColor: '#3766a8',
                      color: 'white',
                      padding: '0.625rem 1.25rem',
                      borderRadius: '0.75rem',
                      border: 'none',
                      fontSize: isMobileView ? '12px' : '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: isMobileView ? '0.375rem' : '0.5rem',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = '#2d5389')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = '#3766a8')
                    }>
                    <Upload className="h-4 w-4" />
                    Upload Images
                  </button>
                </div>
              )}
            </div>
          </Card>
        </div>

        {selectedImages.length > 0 && imageSource !== 'stock' && (
          <div
            className="fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-[calc(100vw-1.5rem)] sm:w-auto max-w-md sm:max-w-none"
            style={{
              backgroundColor: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '1rem',
              boxShadow:
                '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
              padding: isMobileView ? '0.875rem 1rem' : '1rem 1.5rem'
            }}>
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <CheckCircle2
                style={{ width: '20px', height: '20px', color: '#3766a8' }}
              />
              <span
                style={{
                  fontSize: isMobileView ? '14px' : '16px',
                  fontWeight: '500',
                  color: '#1a1a1a'
                }}>
                <span style={{ fontWeight: '700' }}>
                  {selectedImages.length}
                </span>{' '}
                image(s) selected
              </span>
            </div>
            <div
              style={{
                height: isMobileView ? '1px' : '24px',
                width: isMobileView ? '100%' : '1px',
                backgroundColor: '#e5e7eb'
              }}
            />
            <button
              onClick={handleDeleteSelected}
              style={{
                borderRadius: '0.75rem',
                padding: '0.625rem 1rem',
                fontSize: isMobileView ? '12px' : '14px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobileView ? '0.375rem' : '0.5rem',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                fontWeight: '600',
                height: '44px'
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = '#dc2626')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = '#ef4444')
              }>
              <Trash2 className="h-4 w-4" />
              Delete Selected
            </button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => setSelectedImages([])}
              className="w-full sm:w-auto"
              style={{
                borderRadius: '0.75rem',
                height: '44px',
                fontSize: isMobileView ? '12px' : '14px',
                fontWeight: '600'
              }}>
              Clear Selection
            </Button>
          </div>
        )}
      </div>

      {/* Delete Single Image Confirmation Modal */}
      <Dialog
        open={deleteConfirmModalOpen}
        onOpenChange={setDeleteConfirmModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Delete Image
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Are you sure you want to delete this image?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={confirmDeleteImage}>
              OK
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteConfirmModalOpen(false);
                setImageToDelete(null);
              }}
              className="px-8 h-11 text-base font-semibold rounded-lg">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Error Modal */}
      <Dialog
        open={deleteErrorModalOpen}
        onOpenChange={setDeleteErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Error!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Failed to delete image.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setDeleteErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Multiple Confirmation Modal */}
      <Dialog
        open={deleteMultipleConfirmModalOpen}
        onOpenChange={setDeleteMultipleConfirmModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Delete Images
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              {`Are you sure you want to delete ${selectedImages.length} image(s)?`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={confirmDeleteMultiple}>
              OK
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteMultipleConfirmModalOpen(false)}
              className="px-8 h-11 text-base font-semibold rounded-lg">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Multiple Error Modal */}
      <Dialog
        open={deleteMultipleErrorModalOpen}
        onOpenChange={setDeleteMultipleErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Error!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Failed to delete some images.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setDeleteMultipleErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Error Modal */}
      <Dialog
        open={uploadErrorModalOpen}
        onOpenChange={setUploadErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Error!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Upload failed.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setUploadErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Images;
